import { ColDef } from "ag-grid-community";
import { IUploaderColumnConfig, MappingOptions, ValidationResult } from "../../types/types";

/**
 * Maps uploader field configurations to AG Grid column definitions
 * @param fields - The uploader column configurations
 * @param options - Additional options for mapping
 * @returns Array of AG Grid column definitions
 */
export function mapFieldsToColDefs(fields: IUploaderColumnConfig[], options: MappingOptions = {}): ColDef[] {
  const { entityRelations = {}, validationResult, editable = true, onCellValueChanged } = options;

  const colDefs: ColDef[] = [
    {
      headerName: "",
      field: "__selection__",
      width: 40,
      pinned: "left",
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      headerCheckboxSelectionFilteredOnly: true
    },
    {
      headerName: "#",
      field: "__rowNum__",
      width: 80,
      pinned: "left",
      sortable: false,
      filter: false,
      resizable: false,
      suppressMenu: true,
      valueGetter: (params: any) => params.data.id
    },
    {
      headerName: "Errors",
      field: "__errors__",
      width: 200,
      pinned: "left",
      sortable: true,
      filter: true,
      resizable: true,
      autoHeight: true,
      cellClass: "uploader-validation-cell error-cell",
      valueGetter: (params: any) => {
        const rowErrors = validationResult?.errors?.filter(e => e.rowId === params.data.id) || [];
        return rowErrors.map(e => e.message).join("\r\n");
      },
      tooltipValueGetter: (params: any) => {
        const rowErrors = validationResult?.errors?.filter(e => e.rowId === params.data.id) || [];
        return rowErrors.map(e => `${e.field}: ${e.message}`).join("\r\n");
      }
    },
    {
      headerName: "Warnings",
      field: "__warnings__",
      width: 200,
      pinned: "left",
      sortable: true,
      filter: true,
      resizable: true,
      autoHeight: true,
      cellClass: "uploader-validation-cell warning-cell",
      valueGetter: (params: any) => {
        const rowWarnings =
          [...(validationResult?.conflicts || []), ...(validationResult?.duplicates || [])].filter(
            e => e.rowId === params.data.id
          ) || [];
        return rowWarnings.map((e, i) => `${rowWarnings?.length <= 1 ? "" : i + 1 + "."} ${e.message}`).join("\r\n");
      },
      tooltipValueGetter: (params: any) => {
        const rowWarnings =
          [...(validationResult?.conflicts || []), ...(validationResult?.duplicates || [])].filter(
            e => e.rowId === params.data.id
          ) || [];
        return rowWarnings
          .map((e, i) => `${rowWarnings?.length <= 1 ? "" : i + 1 + "."} ${e.field}: ${e.message}`)
          .join("\r\n");
      }
    }
  ];

  fields.forEach(field => {
    const colDef: ColDef = {
      headerName: field.name,
      field: field.key,
      sortable: true,
      filter: true,
      resizable: true,
      editable: editable,
      width: getColumnWidth(field),
      headerTooltip: field.required ? `${field.name} (Required)` : field.name,

      // cell class rules for validation
      cellClassRules: {
        "cell-error": params => {
          const cellErrors = validationResult?.errors?.some(
            error => error.field === field.key && params.data.id === error.rowId
          );
          return cellErrors;
        },
        "cell-warning": params => {
          const cellWarnings = [...(validationResult?.conflicts || []), ...(validationResult?.duplicates || [])]?.some(
            error => error.field === field.key && params.data.id === error.rowId
          );
          return cellWarnings;
        },
        "cell-entity-relation": () => field.type === "entityRelation"
      },
      ...(field.key === "__rowNum__" || field.key === "__actions__" ? { editable: false } : {}),

      ...getTypeSpecificProps(field, entityRelations),

      valueFormatter: params => formatCellValue(params, field),
      cellEditor: getCellEditor(field, entityRelations),
      cellEditorParams: getCellEditorParams(field, entityRelations),
      onCellValueChanged: onCellValueChanged
    };

    if (field.required) {
      colDef.headerClass = "required-column-header";
    }

    colDefs.push(colDef);
  });

  // colDefs.push({
  //   headerName: "Actions",
  //   field: "__actions__",
  //   width: 100,
  //   pinned: "right",
  //   sortable: false,
  //   filter: false,
  //   resizable: false,
  //   suppressMenu: true
  //   // cellRenderer: (params: any) => {
  //   //   const button = document.createElement('button');
  //   //   button.innerHTML = '🗑️ Delete';
  //   //   button.className = 'btn-delete-row';
  //   //   button.onclick = () => {
  //   //     if (params.context?.onDeleteRow) {
  //   //       params.context.onDeleteRow(params.data.id);
  //   //     }
  //   //   };
  //   //   return button;
  //   // }
  // });

  return colDefs;
}

/**
 * Get column width based on field type
 */
function getColumnWidth(field: IUploaderColumnConfig): number {
  switch (field.type) {
    case "boolean":
      return 100;
    case "number":
      return 120;
    case "date":
      return 150;
    case "entityRelation":
      return 200;
    case "text":
    default:
      return 180;
  }
}

/**
 * Get type-specific column properties
 */
function getTypeSpecificProps(field: IUploaderColumnConfig, entityRelations: Record<string, any[]>): Partial<ColDef> {
  switch (field.type) {
    case "boolean":
      return {
        cellRenderer: "agCheckboxCellRenderer",
        cellEditor: "agCheckboxCellEditor"
      };

    case "number":
      return {
        filter: "agNumberColumnFilter",
        valueParser: params => {
          const value = Number(params.newValue);
          return isNaN(value) ? null : value;
        }
      };

    case "date":
      return {
        filter: "agDateColumnFilter",
        valueParser: params => {
          // todo
          return params.newValue;
        }
      };

    case "entityRelation":
      // todo: maybe use autocompleteEditor
      const entities = entityRelations[field.key] || [];
      return {
        cellEditor: "agSelectCellEditor",
        cellEditorParams: {
          values: entities.map(e => e.name || e.value || e)
        }
      };

    default:
      return {
        filter: "agTextColumnFilter"
      };
  }
}

/**
 * Get cell editor based on field type
 */
function getCellEditor(field: IUploaderColumnConfig, entityRelations: Record<string, any[]>): string {
  switch (field.type) {
    case "boolean":
      return "agCheckboxCellEditor";
    case "number":
      return "agNumberCellEditor";
    case "date":
      return "agDateCellEditor";
    case "entityRelation":
      return "agSelectCellEditor";
    default:
      return "agTextCellEditor";
  }
}

/**
 * Get cell editor params based on field type
 */
function getCellEditorParams(field: IUploaderColumnConfig, entityRelations: Record<string, any[]>): any {
  if (field.type === "entityRelation") {
    const entities = entityRelations[field.key] || [];
    return {
      values: entities.map(e => e.name || e.value || e),
      valueListGap: 0
    };
  }

  if (field.type === "number") {
    return {
      min: 0,
      max: Number.MAX_SAFE_INTEGER,
      precision: 2
    };
  }

  return undefined;
}

/**
 * Format cell value for display
 */
function formatCellValue(params: any, field: IUploaderColumnConfig): string {
  const value = params.value;

  if (value === null || value === undefined || value === "") {
    return field.required ? "(Required)" : "";
  }

  switch (field.type) {
    case "boolean":
      return value ? "Yes" : "No";
    case "date":
      // format date
      return value;
    case "number":
      return Number(value).toLocaleString();
    default:
      return String(value);
  }
}

/**
 * Check if cell has validation error
 */
// eslint-disable-next-line
function hasValidationError(params: any, fieldKey: string, validationResult: ValidationResult): boolean {
  const rowErrors = validationResult?.errors.filter(ver => ver.rowId === params.data.id) || [];
  return rowErrors.some(error => error.field === fieldKey);
}

/*?*
 * Create a column definition for row selection
 */
export function getSelectionColDef(): ColDef {
  return {
    headerName: "",
    field: "__selection__",
    width: 50,
    pinned: "left",
    sortable: false,
    filter: false,
    resizable: false,
    suppressMenu: true,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true
  };
}

/**
 * Create column definitions with selection
 */
export function mapFieldsToColDefsWithSelection(
  fields: IUploaderColumnConfig[],
  options: MappingOptions = {}
): ColDef[] {
  const colDefs = mapFieldsToColDefs(fields, options);

  colDefs.splice(1, 0, getSelectionColDef());

  return colDefs;
}
